import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getKeywords, getKeywordSyncStatus, syncKeywords, getKeywordCountries, pullKeywordGeo, type PlayApp,
} from '@/api/play';
import { countryName, flagOf } from '@/components/tracker/format';

// Google's catch-all buckets in the console's search-term breakdown.
const SPECIAL_TERMS: Record<string, string> = {
  REMOVED_BY_THRESHOLDING_STORE_QUERY: 'Other (too rare for Google to show)',
  INVALID_STORE_QUERY: 'No search term',
};

function ago(sec: number): string {
  const m = Math.round((Date.now() / 1000 - sec) / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  return h < 48 ? `${h} h ago` : `${Math.round(h / 24)} days ago`;
}

function isoDaysAgo(days: number): string {
  return new Date(Date.now() - days * 86400_000).toISOString().slice(0, 10);
}

export function KeywordsPanel({ app }: { app: PlayApp }) {
  const qc = useQueryClient();
  const [from, setFrom] = useState(isoDaysAgo(90));
  const [to, setTo] = useState(isoDaysAgo(0));
  const [country, setCountry] = useState('');

  const kw = useQuery({
    queryKey: ['play-keywords', app.id, from, to, country],
    queryFn: () => getKeywords(app.id, from, to, country || undefined),
  });
  const geo = useQuery({
    queryKey: ['play-keyword-countries', app.id, from, to],
    queryFn: () => getKeywordCountries(app.id, from, to),
  });
  const pull = useMutation({
    mutationFn: () => pullKeywordGeo(app.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['play-keyword-countries', app.id] }),
  });
  const status = useQuery({
    queryKey: ['play-keyword-status', app.id],
    queryFn: () => getKeywordSyncStatus(app.id),
  });
  const sync = useMutation({
    mutationFn: () => syncKeywords(app.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['play-keywords', app.id] });
      qc.invalidateQueries({ queryKey: ['play-keyword-status', app.id] });
    },
  });

  const latest = status.data?.[0];
  const rows = kw.data ?? [];
  const countries = geo.data?.countries ?? [];
  const picked = countries.find((c) => c.country === country);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-sm text-content-muted">
          From
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
            className="ml-2 bg-surface border border-border rounded-md px-2 py-1 text-content" />
        </label>
        <label className="text-sm text-content-muted">
          To
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
            className="ml-2 bg-surface border border-border rounded-md px-2 py-1 text-content" />
        </label>
        <button
          onClick={() => sync.mutate()}
          disabled={sync.isPending}
          className="px-3 py-1.5 rounded-md text-sm bg-primary text-white disabled:opacity-50"
        >
          {sync.isPending ? 'Syncing…' : 'Refresh from Play'}
        </button>
        {latest && (
          <span className="text-xs text-content-muted">
            last sync: {latest.status ?? '—'}
            {latest.error ? ` (${latest.error})` : ''}
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm text-content-muted">
          Country
          <select value={country} onChange={(e) => setCountry(e.target.value)}
            className="ml-2 bg-surface border border-border rounded-md px-2 py-1 text-content">
            <option value="">All countries</option>
            {countries.map((c) => (
              <option key={c.country} value={c.country}>
                {flagOf(c.country)} {countryName(c.country)} · {c.visitors.toLocaleString()} visitors
              </option>
            ))}
          </select>
        </label>
        {geo.data?.can_pull && (
          <button
            onClick={() => pull.mutate()}
            disabled={pull.isPending || geo.data.queued}
            className="px-3 py-1.5 rounded-md text-sm border border-border text-content disabled:opacity-50"
          >
            {geo.data.queued ? 'Queued for the runner' : 'Pull countries from the console'}
          </button>
        )}
        <span className="text-xs text-content-muted">
          {geo.data?.synced_at ? `by country: pulled ${ago(geo.data.synced_at)}` : geo.data?.can_pull
            ? 'by country: not pulled yet — the runner pulls once a day'
            : 'by country needs the console’s browser profile (Play publish → Console access)'}
          {geo.data?.error ? ` · last pull failed: ${geo.data.error}` : ''}
        </span>
      </div>

      {pull.isError && (
        <div className="text-sm text-red-500">{(pull.error as Error)?.message || 'could not queue the pull'}</div>
      )}
      {picked && (
        <div className="text-sm text-content-muted">
          {flagOf(picked.country)} {countryName(picked.country)}: {picked.visitors.toLocaleString()} visitors,{' '}
          {picked.acquisitions.toLocaleString()} installs from the store listing
          {picked.visitors ? ` (${((picked.acquisitions / picked.visitors) * 100).toFixed(1)}%)` : ''}
        </div>
      )}

      {sync.isError && (
        <div className="text-sm text-red-500">
          {(sync.error as Error)?.message || 'sync failed'}
        </div>
      )}

      {kw.isLoading ? (
        <div className="text-content-muted text-sm">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-content-muted text-sm">
          {country
            ? 'No search terms for this country in the range.'
            : 'No keyword data yet. Set the account’s Cloud Storage bucket id, then “Refresh from Play”. Play reports lag 3–7 days.'}
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-content-muted border-b border-border">
              <th className="py-2">Keyword</th>
              <th className="py-2 text-right">Visitors</th>
              <th className="py-2 text-right">Installs</th>
              <th className="py-2 text-right">Conversion</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.keyword} className="border-b border-border/50">
                <td className="py-2">
                  {SPECIAL_TERMS[r.keyword]
                    ? <span className="text-content-muted italic">{SPECIAL_TERMS[r.keyword]}</span>
                    : r.keyword}
                </td>
                <td className="py-2 text-right">{r.visitors.toLocaleString()}</td>
                <td className="py-2 text-right">{r.acquisitions.toLocaleString()}</td>
                <td className="py-2 text-right">{(r.conversion_rate * 100).toFixed(1)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
